/**
 * Animaciones de aparición al hacer scroll ("reveal"), sin librerías.
 *
 * Uso en JSX:  <div data-reveal="up">…</div>
 *   variantes: up (por defecto) | down | left | right | zoom | fade
 *   escalonado: data-reveal-delay="1".."8"  (×100 ms)
 *
 * Funcionamiento:
 *  - initReveal() añade la clase `js-reveal` al <html>; sólo entonces el CSS oculta los
 *    elementos [data-reveal] (sin JS —o en el HTML prerenderizado— todo se ve).
 *  - Un IntersectionObserver añade `.is-visible` cuando el elemento entra en pantalla.
 *  - Un MutationObserver observa los elementos que React añade después (cambio de ruta).
 *  - Respeta prefers-reduced-motion (muestra todo sin animar).
 */
let io = null;
let mo = null;

const reveal = (el) => {
    el.classList.add('is-visible');
    if (io) io.unobserve(el);
};

const observe = (root) => {
    if (!io) return;
    root.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el) => {
        // Si ya está en pantalla al montar (p. ej. el hero), se revela enseguida.
        io.observe(el);
    });
};

export const initReveal = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (document.documentElement.classList.contains('js-reveal')) return;
    if (window.__PRERENDERING__) return;

    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) return; // sin clase → todo visible, sin animación

    document.documentElement.classList.add('js-reveal');

    io = new IntersectionObserver(
        (entries) => entries.forEach((e) => e.isIntersecting && reveal(e.target)),
        { threshold: 0, rootMargin: '0px 0px -10% 0px' }
    );

    const root = document.getElementById('root') || document.body;
    observe(root);

    mo = new MutationObserver((mutations) => {
        for (const m of mutations) {
            m.addedNodes.forEach((n) => {
                if (n.nodeType !== 1) return;
                if (n.matches && n.matches('[data-reveal]')) io.observe(n);
                observe(n);
            });
        }
    });
    mo.observe(root, { childList: true, subtree: true });

    // Al cambiar de ruta con scroll arriba, revelar lo que ya está a la vista.
    window.addEventListener('load', () => observe(root));
};

/** Sólo para el prerender/tests: revela todo y desconecta observadores. */
export const revealAll = () => {
    document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
    if (io) io.disconnect();
    if (mo) mo.disconnect();
};
